import { Formik, FormikProps } from 'formik';
import React, { useState } from 'react';
import { Button, Modal } from 'semantic-ui-react';

import { hasIntersection } from 'utils/arrayHelpers';
import { DebugAdminFormikModals } from 'utils/constants';

import { FlexGroup } from 'components/_common/FlexGroup';
import { Typography } from 'components/_common/Typography';
import { LoadingSVG } from 'components/Loading/Loading';

type CustomCrudModalProps<T> = {
  disabled: boolean;
  title?: string;
  actionText?: string;
  seed?: T;
  validationSchema?: any;
  renderForm: (props: FormikProps<T>) => React.ReactElement;
  onSubmitForm?: (data: T) => Promise<void> | void;
};

export function CustomCrudModal<T>({
  disabled,
  title,
  actionText = 'Edit',
  seed,
  validationSchema,
  renderForm,
  onSubmitForm,
}: React.PropsWithChildren<CustomCrudModalProps<T>>): React.ReactElement {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <Modal
      open={isModalOpen}
      onOpen={() => setIsModalOpen(true)}
      onClose={() => setIsModalOpen(false)}
      key={actionText}
      trigger={<Button disabled={disabled}>{actionText}</Button>}
      closeOnEscape
      closeOnDimmerClick={false}
    >
      <Formik<T>
        enableReinitialize
        initialValues={seed || ({} as T)}
        validationSchema={validationSchema}
        onSubmit={(values, { setSubmitting }) => {
          if (!onSubmitForm) {
            setIsModalOpen(false);
            return;
          }
          setSubmitting(true);

          Promise.resolve(onSubmitForm(values))
            .then(() => {
              setIsModalOpen(false);
            })
            .finally(() => {
              setSubmitting(false);
            });
        }}
      >
        {formikProps => {
          const {
            values,
            errors,
            touched,
            isSubmitting,
            submitForm,
            isValid,
            status,
            submitCount,
          } = formikProps;
          const hasErrors = hasIntersection(Object.keys(errors), Object.keys(touched));
          if (submitCount > 0) Object.assign(touched, { ...touched }, { ...errors });
          return (
            <>
              <Modal.Content>
                {title && (
                  <Typography variant="h4" color="stone90">
                    {title}
                  </Typography>
                )}
                {renderForm(formikProps)}
                {DebugAdminFormikModals && (
                  <div>
                    <pre>{JSON.stringify(values, null, 2)}</pre>
                    <pre>{JSON.stringify(errors, null, 2)}</pre>
                    <pre>{JSON.stringify(touched, null, 2)}</pre>
                    <pre>isValid: {JSON.stringify(isValid, null, 2)}</pre>
                    <pre>status: {JSON.stringify(status, null, 2)}</pre>
                    <pre>submitCount: {JSON.stringify(submitCount, null, 2)}</pre>
                  </div>
                )}
              </Modal.Content>
              <Modal.Actions>
                <Button onClick={() => setIsModalOpen(false)}>Cancel</Button>
                <Button
                  primary
                  disabled={hasErrors || !isValid || isSubmitting}
                  onClick={() => {
                    submitForm();
                  }}
                >
                  <FlexGroup
                    prefix={isSubmitting && <LoadingSVG width={12} height={12} />}
                    alignItems="center"
                  >
                    Save
                  </FlexGroup>
                </Button>
              </Modal.Actions>
            </>
          );
        }}
      </Formik>
    </Modal>
  );
}
